/** @file Opens a PSD in a Web Worker so ag-psd never blocks the editor; the same PsdSession as in-thread. */
import { makeError } from '@pose2d/format/pose2dError';
import type { Pose2DError } from '@pose2d/format/types';
import type { PsdSession } from './importTypes';
import type { PsdWorkerReply, WorkerLike } from './psdWorkerProtocol';

type Png = Uint8Array<ArrayBuffer>;
type Waiter = { resolve: (png: Png) => void; reject: (error: Error) => void };

const spawnPsdWorker = (): WorkerLike => new Worker(new URL('./psdWorker.ts', import.meta.url), { type: 'module' }) as unknown as WorkerLike;
const asError = (error: Pose2DError): Error => Object.assign(new Error(error.message), { pose2d: error });

/** Layer requests by id; once `failAll` ran (close or crash) every later request rejects at once instead of hanging. */
function createRequests(worker: WorkerLike) {
  const waiting = new Map<number, Waiter>();
  const dead: { error: Error | null } = { error: null };
  let nextId = 1;
  return {
    waiting,
    failAll: (error: Error): void => {
      dead.error = error;
      for (const w of waiting.values()) w.reject(error);
      waiting.clear();
    },
    request: (index: number): Promise<Png> =>
      new Promise((resolve, reject) => {
        if (dead.error !== null) {
          reject(dead.error);
          return;
        }
        const id = nextId++;
        waiting.set(id, { resolve, reject });
        worker.postMessage({ type: 'layer', id, index }, []);
      }),
  };
}

/**
 * Transfers `bytes` (they are detached afterwards) and resolves once the structure is read; layer replies are matched
 * to requests by id, so concurrent requests are safe. A failed open terminates the worker and rejects; after close()
 * or a crash, waiting and later layer requests reject.
 */
export function openPsdInWorker(bytes: Uint8Array<ArrayBuffer>, spawn: () => WorkerLike = spawnPsdWorker): Promise<PsdSession> {
  const worker = spawn();
  const { waiting, failAll, request } = createRequests(worker);
  const session = (reply: Extract<PsdWorkerReply, { type: 'opened' }>): PsdSession => ({
    width: reply.width,
    height: reply.height,
    layers: reply.layers,
    warnings: reply.warnings,
    layerPng: request,
    close: () => {
      worker.terminate();
      failAll(asError(makeError('NOT_FOUND', { kind: 'PSD session', ref: 'closed' })));
    },
  });
  return new Promise((resolve, reject) => {
    worker.onmessage = ({ data }) => {
      if (data.type === 'opened') {
        resolve(session(data));
        return;
      }
      const waiter = waiting.get(data.id);
      waiting.delete(data.id);
      if (data.type === 'layer') waiter?.resolve(data.png);
      else if (data.id !== 0) waiter?.reject(asError(data.error));
      else {
        worker.terminate();
        reject(asError(data.error));
      }
    };
    worker.onerror = () => {
      const error = asError(makeError('INTERNAL', undefined, { hint: 'the PSD worker failed to start or crashed' }));
      worker.terminate();
      failAll(error);
      reject(error);
    };
    worker.postMessage({ type: 'open', bytes }, [bytes.buffer]);
  });
}
